import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BadgeCheck, Radio, ArrowRight, Heart, Bell, Target } from "lucide-react";

export default function Streamers() {
  const { user } = useAuth();
  const isAuthed = user && user !== false;
  const [streamers, setStreamers] = useState([]);
  const [followed, setFollowed] = useState(new Set());

  const load = () => {
    api.get("/streamers").then((r) => setStreamers(r.data));
    if (isAuthed) api.get("/me/follows").then((r) => setFollowed(new Set(r.data)));
  };
  useEffect(() => { load(); }, [isAuthed]); // eslint-disable-line

  const toggleFollow = async (e, s) => {
    e.preventDefault(); e.stopPropagation();
    if (!isAuthed) { toast.error("Log in to follow streamers."); return; }
    const isFol = followed.has(s.id);
    try {
      if (isFol) {
        await api.delete(`/streamers/${s.id}/follow`);
        setFollowed((prev) => { const n = new Set(prev); n.delete(s.id); return n; });
        toast("Unfollowed " + s.handle);
      } else {
        await api.post(`/streamers/${s.id}/follow`);
        setFollowed((prev) => new Set(prev).add(s.id));
        toast.success("Following " + s.handle);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const pinned = streamers.filter((s) => followed.has(s.id));
  const liveFollowed = pinned.filter((s) => s.is_live);
  const others = streamers.filter((s) => !followed.has(s.id));

  const Card = ({ s, pinnedCard = false }) => {
    const isFol = followed.has(s.id);
    return (
      <Link
        to={`/streamers/${s.id}`}
        data-testid={`streamer-card-${s.handle}`}
        className={`group border ${pinnedCard ? "border-cyan-400/50" : "border-[#232330]"} bg-[#101015] overflow-hidden hover:-translate-y-0.5 transition-transform block`}
      >
        {s.avatar_url ? (
          <div className="aspect-video overflow-hidden">
            <img
              src={s.avatar_url}
              alt={s.handle}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        ) : (
          <div
            data-testid={`streamer-no-avatar-${s.handle}`}
            className="aspect-video flex items-center justify-center bg-gradient-to-br from-[#101015] via-[#0d0d18] to-[#101015] border-b border-[#232330]"
          >
            <div className="flex flex-col items-center gap-2 text-[#B084FF]">
              <Radio className="w-8 h-8 opacity-60" />
              <span className="font-heading font-black text-xl uppercase tracking-tight text-white/90">{s.handle}</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">No avatar uploaded</span>
            </div>
          </div>
        )}
        <div className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Radio className={`w-4 h-4 ${s.is_live ? "text-red-500 animate-pulse" : "text-[#FF007F]"}`} />
              <span className="font-heading font-black text-lg uppercase tracking-tight">{s.handle}</span>
            </div>
            <div className="flex items-center gap-2">
              {s.is_live && (
                <span
                  data-testid={`live-badge-${s.handle}`}
                  className="bg-red-600 text-white text-[10px] font-mono font-bold tracking-widest px-2 py-0.5 flex items-center gap-1"
                >
                  <span className="inline-block w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                  LIVE
                </span>
              )}
              {s.verified && <BadgeCheck className="w-4 h-4 text-cyan-400" />}
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-1 font-mono tracking-widest">{s.platform} · {s.country}</div>
          <div className="text-sm text-gray-400 mt-3 line-clamp-2">{s.bio || "No dossier yet."}</div>
          <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-[#232330]">
            <div>
              <div className="text-[9px] text-gray-500 uppercase tracking-widest font-mono">Followers</div>
              <div className="font-mono font-bold text-white">{s.followers?.toLocaleString?.() || 0}</div>
            </div>
            <div>
              <div className="text-[9px] text-gray-500 uppercase tracking-widest font-mono">Hours</div>
              <div className="font-mono font-bold text-white">{s.hours_streamed}</div>
            </div>
          </div>
          <button
            onClick={(e) => toggleFollow(e, s)}
            data-testid={`follow-${s.handle}`}
            className={`mt-4 w-full flex items-center justify-center gap-2 text-[10px] font-mono tracking-widest uppercase font-bold py-2 border transition-colors ${
              isFol
                ? "border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black"
                : "border-[#232330] text-gray-400 hover:border-[#FF007F] hover:text-[#FF007F]"
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${isFol ? "fill-current" : ""}`} />
            {isFol ? "Following" : "Follow"}
          </button>
        </div>
      </Link>
    );
  };

  return (
    <div className="space-y-8" data-testid="streamers-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[#F4D03F] font-mono tracking-[0.3em] text-xs mb-2">/ BROADCAST WING</div>
          <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Streamers</h1>
        </div>
        <Link to="/streamers/signup">
          <Button
            data-testid="streamer-signup-cta"
            className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold"
          >
            Sign up <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>

      {isAuthed && liveFollowed.length > 0 && (
        <div
          data-testid="live-notification-banner"
          className="border border-red-500/60 bg-red-500/5 p-4 flex items-start gap-3"
        >
          <Bell className="w-5 h-5 text-red-500 animate-pulse shrink-0 mt-0.5" />
          <div>
            <div className="font-heading font-black uppercase tracking-tight text-sm text-red-500">
              {liveFollowed.length} of your follows {liveFollowed.length === 1 ? "is" : "are"} LIVE right now
            </div>
            <div className="text-xs text-gray-400 mt-1 font-mono">
              {liveFollowed.map((s) => s.handle).join(" · ")}
            </div>
          </div>
        </div>
      )}

      {isAuthed && pinned.length > 0 && (
        <section>
          <div className="text-[10px] font-mono tracking-widest text-cyan-400 uppercase mb-3 flex items-center gap-2">
            <Target className="w-3 h-3" /> Your follows
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pinned.map((s) => <Card key={s.id} s={s} pinnedCard />)}
          </div>
        </section>
      )}

      <section>
        {isAuthed && pinned.length > 0 && (
          <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase mb-3">Discover</div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {others.map((s) => <Card key={s.id} s={s} />)}
          {streamers.length === 0 && (
            <div className="col-span-full border border-[#232330] p-8 text-center text-gray-500">
              No streamers signed yet.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
